import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { Select2ScrollEvent, Select2SearchEvent } from 'ng-select2-component';
import { IBase, IClient, ITown } from 'src/app/models';
import { Colors, ExpeditionStatus, ExpeditionType, InputType } from 'src/app/models/enums';
import { RouteService, StorageService, TripsService, UsersService, UtilsService } from 'src/app/services';
import { LiteralService } from 'src/app/services/literal/literal.service';
import { ServiceInfoService } from 'src/app/services/service-info/service-info.service';
import { TooltipPosition } from 'src/app/shared/tooltip/tooltip-position';
import { images } from 'src/images';
import {
  BOOKING_FILTER_KEYS,
  IBookingFilters,
  deserializeBookingFilters,
  hasAnyFilterKey,
  parseBookingFiltersFromStorage,
} from 'src/app/filters';

@Component({
    selector: 'app-booking-filter',
    templateUrl: './filter.component.html',
    styleUrl: './filter.component.scss',
    standalone: false
})
export class BookingFilterComponent implements OnChanges, OnDestroy {
  private queryParamsSub?: Subscription;
  private servicesLoadedForTownsKey: string | null = null;
  private initialized = false;
  public expeditionStatus: any[] = [];
  public expeditionStatusCopy: any[] = [];
  public expeditionTypes: any[] = [];
  public ExpeditionStatus = ExpeditionStatus;
  public ExpeditionTypes = ExpeditionType;
  public closedStatusSelected = false;
  public cancelledStatusSelected = false;
  public faultsSelected = false;

  public clients: unknown[] = [];
  public selectedValuesClients: IBase[] = [];
  public towns: unknown[] = [];
  public allTowns: unknown[] = [];
  public selectedValuesTowns: IBase[] = [];
  public origin = '';
  public destination = '';

  public servicesPage = 1;
  public servicesSearchValue = '';
  public servicesElement: any = {
    id: 'bookingServices',
    value: [],
    edit: {
      editable: true,
      inputType: InputType.MULTISELECT_SCROLL,
      values: [],
      valueToChange: 'services',
      customSearchEnabled: true,
      disabled: true,
      placeholder: '-'
    }
  };

  public routesPage = 1;
  public routesSearchValue = '';
  public routesElement: any = {
    id: 'bookingRoutes',
    value: [],
    edit: {
      editable: true,
      inputType: InputType.MULTISELECT_SCROLL,
      values: [],
      valueToChange: 'routes',
      customSearchEnabled: true,
      disabled: true,
      placeholder: '-'
    }
  };

  public images = images;
  public TooltipPosition = TooltipPosition;

  public filterSelected = 'all';
  public filters: any[] = [];
  public showClients: boolean = false;

  public colors = Colors;

  @Output() filter = new EventEmitter();
  @Output() closeFilter = new EventEmitter();

  @Input() componentOpen: string;
  @Input() isFilterOpen: boolean = false;

  @ViewChild('select') select: ElementRef;

  constructor(
    renderer: Renderer2,
    public literalService: LiteralService,
    public tripsService: TripsService,
    public utilsService: UtilsService,
    public usersService: UsersService,
    public storageService: StorageService,
    public serviceInfoService: ServiceInfoService,
    public routeService: RouteService,
    private activatedRoute: ActivatedRoute,
  ) {
    this.expeditionStatus.push({value: 'Open', checked: true });
    this.expeditionStatus.push({value: 'Ready', checked: true });
    this.expeditionStatus.push({value: 'Ongoing', checked: true });
    
    (Object.keys(ExpeditionType) as Array<keyof typeof ExpeditionType>).map(
      (expeditionTypes: keyof typeof ExpeditionType) => {
        if (expeditionTypes) this.expeditionTypes.push({ value: expeditionTypes, checked: false });
      },
    );

    renderer.listen('window', 'click', (e: Event) => {
      const modal = document.getElementById('filterContainer')!;
      const componentOpen = document.getElementById(this.componentOpen)!;
      const close = utilsService.closeCustomModals(e, modal, componentOpen);
      if (close) {
        this.closeFilter.emit();
        this.initFilters();
        this.select.nativeElement.checked = false;
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isFilterOpen']?.currentValue && !this.initialized) {
      this.initialized = true;
      this.lazyInit();
    }
  }

  ngOnDestroy(): void {
    this.queryParamsSub?.unsubscribe();
  }

  private async lazyInit(): Promise<void> {
    const { clients, towns } = await this.usersService.getPossibleClientTowns();
    clients.map((client: IClient) => {
      this.clients.push({
        id: client.id,
        code: client.code,
        name: client.name,
      });
    });
    towns.map((town: ITown) => {
      this.towns.push({ id: town.id, code: town.name, name: town.name });
    });
    this.clients.sort(this.utilsService.compare('name'));
    this.towns.sort(this.utilsService.compare('name'));
    this.allTowns = [...this.towns];

    this.showClients = clients.length > 1;

    this.initFilters();
    this.queryParamsSub = this.activatedRoute.queryParamMap.subscribe(() => this.initFilters());
  }

  async onClientsChange() {
    if (this.selectedValuesClients.length === 0) {
      this.towns = [...this.allTowns];
      this.selectedValuesTowns = this.selectedValuesTowns.filter((town: IBase) =>
        (this.towns as any[]).some((t: any) => t.id === town.id),
      );
      return;
    }

    const clientIds = this.selectedValuesClients.map((client: IBase) => client.id!);
    const towns = (await this.usersService.getTownsByClient(clientIds)) as ITown[];
    this.towns = towns
      .map((town: ITown) => ({ id: town.id, code: town.name, name: town.name }))
      .sort(this.utilsService.compare('name'));
    this.selectedValuesTowns = this.selectedValuesTowns.filter((town: IBase) =>
      (this.towns as any[]).some((t: any) => t.id === town.id),
    );
  }

  async loadServices(showMore = false) {
    const towns = this.selectedValuesTowns.length > 0
      ? this.selectedValuesTowns.map((t: IBase) => ({ id: t.id }))
      : (this.towns as any[]).map((t: any) => ({ id: t.id }));
    if (towns.length === 0) {
      this.servicesElement.edit.values = [];
      return;
    }
    const result = await this.serviceInfoService.getServiceInfosSearch(10, this.servicesPage, towns as ITown[], this.servicesSearchValue || undefined);
    const content = (result as any).content || result;
    const newValues = (content as any[]).map((s: any) => ({
      id: s.id,
      value: { id: s.id, tag: s.tag, name: s.name },
      label: s.name ? `${s.tag}: ${s.name}` : s.tag
    }));
    this.servicesElement.edit.values = (showMore
      ? [...this.servicesElement.edit.values, ...newValues]
      : newValues
    ).sort(this.utilsService.compare('label'));
  }

  async loadRoutes(showMore = false) {
    const serviceIds = (this.servicesElement.value as any[]).map((s: any) => s.id);
    if (serviceIds.length === 0) {
      this.routesElement.edit.values = [];
      return;
    }
    const result = await this.routeService.getRoutesSearch(serviceIds, this.routesPage, 10, this.routesSearchValue || undefined);
    const content = (result as any).content || result;
    const newValues = (content as any[]).map((r: any) => ({
      id: r.id,
      value: { id: r.id, name: r.name },
      label: r.name
    }));
    this.routesElement.edit.values = (showMore
      ? [...this.routesElement.edit.values, ...newValues]
      : newValues
    ).sort(this.utilsService.compare('label'));
  }

  async onTownsClose() {
    this.servicesElement.value = [];
    this.servicesElement.edit.values = [];
    this.servicesElement.edit.disabled = this.selectedValuesTowns.length === 0;
    this.routesElement.value = [];
    this.routesElement.edit.values = [];
    this.routesElement.edit.disabled = true;
    this.servicesPage = 1;
    this.servicesSearchValue = '';
    if (!this.servicesElement.edit.disabled) await this.loadServices();
  }

  onServicesChange(value: any) {
    this.servicesElement.value = value;
    this.routesElement.value = [];
    this.routesElement.edit.values = [];
    this.routesPage = 1;
    this.routesSearchValue = '';
    this.routesElement.edit.disabled = !value || value.length === 0;
    if (value && value.length > 0) {
      this.loadRoutes();
    }
  }

  onRoutesChange(value: any) {
    this.routesElement.value = value;
  }

  async onScroll(event: Select2ScrollEvent, valueToChange: string) {
    if (event.way === 'down') {
      if (valueToChange === 'services') {
        this.servicesPage += 1;
        await this.loadServices(true);
      } else if (valueToChange === 'routes') {
        this.routesPage += 1;
        await this.loadRoutes(true);
      }
    }
  }

  async onSearch(event: Select2SearchEvent, valueToChange: string) {
    if (valueToChange === 'services') {
      this.servicesPage = 1;
      this.servicesSearchValue = event.search;
      await this.loadServices();
    } else if (valueToChange === 'routes') {
      this.routesPage = 1;
      this.routesSearchValue = event.search;
      await this.loadRoutes();
    }
  }

  async initFilters() {
    const filters = await this.resolveCurrentFilters();
    if (!filters) return;
    this.applyFiltersToView(filters);
  }

  private async resolveCurrentFilters(): Promise<IBookingFilters | null> {
    const params = this.activatedRoute.snapshot.queryParamMap;
    const storageFilters = parseBookingFiltersFromStorage(await this.storageService.getBookingsFilters());
    if (hasAnyFilterKey(params, BOOKING_FILTER_KEYS)) {
      return deserializeBookingFilters(params, storageFilters ?? undefined);
    }
    return storageFilters;
  }

  private applyFiltersToView(selectedFilters: IBookingFilters) {
    this.selectedValuesClients = selectedFilters.selectedClients;
    this.selectedValuesTowns = selectedFilters.selectedTowns;
    this.servicesElement.value = selectedFilters.selectedServices || [];
    this.routesElement.value = selectedFilters.selectedRoutes || [];
    this.cancelledStatusSelected = selectedFilters.cancelled;
    this.faultsSelected = selectedFilters.faults;
    this.origin = selectedFilters.origin;
    this.destination = selectedFilters.destination;

    if (selectedFilters.selectedStatus?.length) {
      this.expeditionStatus.map((status) => {
        status.checked = selectedFilters.selectedStatus.some((s: any) => s === status.value);
      });
      this.closedStatusSelected = selectedFilters.selectedStatus.some((s: any) => s === ExpeditionStatus.CLOSED);
    }
    if (this.cancelledStatusSelected || this.faultsSelected) {
      this.expeditionStatusCopy = structuredClone(this.expeditionStatus);
      this.expeditionStatus.map((status) => status.checked = false);
    } else {
      this.expeditionStatusCopy = this.expeditionStatus;
    }

    this.expeditionTypes.map((type) => {
      type.checked = selectedFilters.selectedTypes.some((t: any) => t === type.value);
    });

    this.servicesElement.edit.disabled = this.selectedValuesTowns.length === 0;

    // Avoid refetching /service-infos/filter on every URL change — only reload when
    // the towns scope actually changed (or this is the first load).
    const townsKey = this.selectedValuesTowns.length > 0
      ? this.selectedValuesTowns.map((t: IBase) => t.id).sort().join(',')
      : '<all>';
    if (townsKey !== this.servicesLoadedForTownsKey) {
      this.servicesLoadedForTownsKey = townsKey;
      this.loadServices().then(() => {
        if (this.servicesElement.value.length > 0) {
          this.routesElement.edit.disabled = false;
          this.loadRoutes();
        }
      });
    } else if (this.servicesElement.value.length > 0) {
      this.routesElement.edit.disabled = false;
    }
  }

  changeFilter(event: any) {
    this.filterSelected = event.target.value;
  };

  onChangeExclusiveStatus(type: string) {
    if (this.closedStatusSelected || this.cancelledStatusSelected || this.faultsSelected) {
      this.expeditionStatus = this.expeditionStatusCopy;
      this.expeditionStatusCopy = [];
      this.expeditionStatus.map((status) => status.checked = [ExpeditionStatus.OPEN, ExpeditionStatus.READY, ExpeditionStatus.ONGOING].includes(status.value));
    } else {
      this.expeditionStatusCopy = structuredClone(this.expeditionStatus);
      this.expeditionStatus.map((status) => status.checked = false);
    }
    if (type === 'closed') this.closedStatusSelected = !this.closedStatusSelected;
    else if (type === 'cancelled') this.cancelledStatusSelected = !this.cancelledStatusSelected;
    else if (type === 'faults') this.faultsSelected = !this.faultsSelected;
  };

  onChangeOD(newValue: any, type: string) {
    if (type === 'origin') {
      this.origin = newValue.target.value;
    } else {
      this.destination = newValue.target.value;
    }
  }

  selectAll(event: any) {
    this.closedStatusSelected = false;
    this.cancelledStatusSelected = false;
    this.faultsSelected = false;
    this.expeditionStatus.map(
      (status: any) => (status.checked = event.currentTarget.checked
        ? true
        : [ExpeditionStatus.OPEN, ExpeditionStatus.READY, ExpeditionStatus.ONGOING].includes(status.value)),
    );
    this.expeditionTypes.map(
      (type: any) => (type.checked = event.currentTarget.checked),
    );
    if (event.currentTarget.checked) {
      this.clients.map((client: any) =>
        this.selectedValuesClients.push({ id: client.id, name: client.code }),
      );
      this.towns.map((town: any) =>
        this.selectedValuesTowns.push({ id: town.id, name: town.code }),
      );
    } else {
      this.selectedValuesClients = [];
      this.selectedValuesTowns = [];
    }
    this.servicesElement.edit.disabled = this.selectedValuesTowns.length === 0;
  };

  clearSelection() {
    this.expeditionStatus.map((status: any) => (status.checked = [ExpeditionStatus.OPEN, ExpeditionStatus.READY, ExpeditionStatus.ONGOING].includes(status.value)));
    this.expeditionTypes.map((type: any) => (type.checked = false));
    this.selectedValuesClients = [];
    this.selectedValuesTowns = [];
    this.servicesElement.value = [];
    this.servicesElement.edit.values = [];
    this.servicesElement.edit.disabled = true;
    this.routesElement.value = [];
    this.routesElement.edit.values = [];
    this.routesElement.edit.disabled = true;
    this.servicesPage = 1;
    this.servicesSearchValue = '';
    this.routesPage = 1;
    this.routesSearchValue = '';
    this.select.nativeElement.checked = false;
    this.closedStatusSelected = false;
    this.cancelledStatusSelected = false;
    this.faultsSelected = false;
    this.expeditionStatusCopy = [];
    this.origin = '';
    this.destination = '';
  };

  async onSubmit() {
    const selectedClients: IBase[] = [];
    this.selectedValuesClients.forEach((client: IBase) => {
      selectedClients.push(client);
    });

    const selectedTowns: IBase[] = [];
    this.selectedValuesTowns.forEach((town: IBase) => {
      selectedTowns.push(town);
    });

    const selectedServices: any[] = (this.servicesElement.value as any[]).map((s: any) => ({
      id: s.id,
      tag: s.tag,
      name: s.name
    }));

    const selectedRoutes: any[] = (this.routesElement.value as any[]).map((r: any) => ({
      id: r.id,
      name: r.name
    }));

    const selectedTypes: string[] = [];
    this.expeditionTypes.forEach((type: any) => {
      type.checked && selectedTypes.push(type.value);
    });

    const selectedStatus = [];
    if (this.closedStatusSelected) selectedStatus.push(ExpeditionStatus.CLOSED);
    else if (!this.cancelledStatusSelected && !this.faultsSelected) {
      this.expeditionStatus.forEach((status: any) => {
        if (status.checked) selectedStatus.push(status.value) ;
      });
    }

    this.storageService.setBookingsFilters(JSON.stringify({
      selectedClients,
      selectedTowns,
      selectedServices,
      selectedRoutes,
      selectedTypes,
      selectedStatus,
      cancelled: this.cancelledStatusSelected,
      faults: this.faultsSelected,
      origin: this.origin,
      destination: this.destination
    }))

    this.filter.emit({selectedClients, selectedTowns, selectedServices, selectedRoutes, selectedTypes, selectedStatus, cancelled: this.cancelledStatusSelected, faults: this.faultsSelected, origin: this.origin, destination: this.destination});
  };
}
